import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import CountUp from "react-countup";
import { Copy, TrendingUp, MessageCircle } from "lucide-react";
import api from "@/lib/api";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M18.9 2H22l-7.6 8.7L23 22h-6.9l-5.4-7-6.2 7H1.2l8.1-9.3L1 2h7.1l4.9 6.4L18.9 2Zm-2.4 18h1.9L8.6 4H6.6l9.9 16Z"/></svg>
);

export default function ReferralLeaderboardHook({ compact = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referrals/me").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const referralLink = `${window.location.origin}/register?ref=${data.referral_code}`;
  const shareMessage = `I'm rank #${data.current_rank} on Cloudivo's leaderboard, grinding a free 30-day career challenge. Join with my link and we both get +${data.reward_xp} XP: ${referralLink}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Referral link copied!");
    } catch (e) {
      toast.error(`Could not copy automatically — copy manually: ${referralLink}`);
    }
  };

  return (
    <div
      data-testid="referral-leaderboard-hook"
      className={`relative overflow-hidden rounded-2xl border border-amber-400/25 p-5 sm:p-6 ${compact ? "" : "mb-8"}`}
      style={{ background: "radial-gradient(circle at 15% 0%, rgba(251,191,36,0.12), rgba(255,255,255,0.02))" }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Invite Friends. Jump the Leaderboard.
          </span>
          <p className="text-white font-heading font-semibold text-lg mt-1.5">
            {data.ranks_gained_per_referral > 0 ? (
              <>Invite 1 friend, leapfrog <span className="text-amber-400" data-testid="referral-ranks-gained"><CountUp end={data.ranks_gained_per_referral} duration={1.2} /></span> people on the board.</>
            ) : (
              <>Every friend who joins earns you both bonus XP.</>
            )}
          </p>
          <p className="text-slate-400 text-xs mt-1">You're currently rank <span className="text-white font-semibold" data-testid="referral-current-rank">#{data.current_rank}</span> — +{data.reward_xp} XP and +{data.reward_coins} coins per completed referral.</p>
        </div>

        <div className="flex flex-col gap-2 shrink-0 w-full sm:w-auto">
          <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-lg px-3 py-2.5">
            <span className="text-xs text-slate-300 flex-1 truncate max-w-[220px]" data-testid="referral-hook-link-text">{referralLink}</span>
            <button onClick={copyLink} data-testid="referral-hook-copy-btn" className="flex items-center gap-1 text-xs font-semibold text-amber-400 hover:text-amber-300 shrink-0 transition-colors">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`} target="_blank" rel="noreferrer"
              data-testid="referral-hook-whatsapp-btn"
              className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-300 text-xs font-semibold py-2 rounded-lg transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`} target="_blank" rel="noreferrer"
              data-testid="referral-hook-twitter-btn"
              className="flex-1 flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white text-xs font-semibold py-2 rounded-lg transition-colors"
            >
              <XIcon className="w-3 h-3" /> Share
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
